/**
 * Drive API v3 hívások. Szándékosan `fetch`-csel, a gapi kliens nélkül —
 * kisebb bundle, kevesebb mozgó alkatrész.
 */
import { CONFIG } from '../config';
import { getToken, invalidateToken } from '../auth/gis';

const API = 'https://www.googleapis.com/drive/v3';
const UPLOAD = 'https://www.googleapis.com/upload/drive/v3';

export interface FileMeta {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  headRevisionId?: string;
  size?: string;
  capabilities?: { canEdit?: boolean };
  webViewLink?: string;
  /** A szülőmappák azonosítói — a létrehozás helyének ellenőrzéséhez. */
  parents?: string[];
  /** Ha közös meghajtón van a fájl, ennek a meghajtónak az azonosítója. */
  driveId?: string;
}

export class DriveError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'DriveError';
  }
}

const META_FIELDS =
  'id,name,mimeType,modifiedTime,headRevisionId,size,capabilities(canEdit),webViewLink,parents,driveId';

/**
 * Közös meghajtók (Shared Drive) támogatása. E nélkül a Drive API a shared drive-os
 * elemeket egyszerűen nem látja: a megnyitás 404-gyel elszáll, a létrehozás pedig a
 * megadott szülőmappa helyett a Saját meghajtó gyökerébe teszi a fájlt.
 * Minden files.* híváson kötelező.
 */
const ALL_DRIVES = 'supportsAllDrives=true';

/** Hozzáfűzi a supportsAllDrives paramétert a megfelelő elválasztóval. */
function withAllDrives(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${ALL_DRIVES}`;
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

interface RequestOptions extends RequestInit {
  resourceKey?: { fileId: string; key: string };
  /** Ne parse-olja JSON-ként, hanem szövegként adja vissza. */
  asText?: boolean;
  retries?: number;
}

async function request<T>(url: string, opts: RequestOptions = {}): Promise<T> {
  const { resourceKey, asText, retries = 3, headers, ...rest } = opts;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const token = await getToken(attempt > 0);
    const h = new Headers(headers);
    h.set('Authorization', `Bearer ${token}`);
    if (resourceKey) {
      h.set('X-Goog-Drive-Resource-Keys', `${resourceKey.fileId}/${resourceKey.key}`);
    }

    let res: Response;
    try {
      res = await fetch(url, { ...rest, headers: h });
    } catch (err) {
      // Hálózati hiba — érdemes újrapróbálni.
      lastError = err;
      if (attempt === retries) break;
      await sleep(2 ** attempt * 500);
      continue;
    }

    if (res.ok) {
      return (asText ? await res.text() : await res.json()) as T;
    }

    if (res.status === 401) {
      invalidateToken();
      if (attempt < retries) continue;
    }

    // 403 rate limit / 5xx → visszalépéses újrapróbálkozás
    if ((res.status === 403 || res.status >= 500) && attempt < retries) {
      await sleep(2 ** attempt * 700);
      continue;
    }

    const body = await res.text().catch(() => '');
    throw new DriveError(driveMessage(res.status, body), res.status);
  }

  throw new DriveError(
    `Hálózati hiba a Drive elérésekor: ${String(lastError ?? 'ismeretlen')}`,
    0,
  );
}

function driveMessage(status: number, body: string): string {
  switch (status) {
    case 403:
      return 'Nincs jogosultságod ehhez a művelethez.';
    case 404:
      return 'A fájl nem található, vagy nincs hozzáférésed.';
    case 429:
      return 'Túl sok kérés a Drive felé, próbáld újra pár másodperc múlva.';
    default:
      return `Drive hiba (${status}): ${body.slice(0, 200)}`;
  }
}

export function getMetadata(fileId: string, resourceKey?: string): Promise<FileMeta> {
  return request<FileMeta>(
    withAllDrives(`${API}/files/${fileId}?fields=${encodeURIComponent(META_FIELDS)}`),
    { resourceKey: resourceKey ? { fileId, key: resourceKey } : undefined },
  );
}

export function getContent(fileId: string, resourceKey?: string): Promise<string> {
  return request<string>(withAllDrives(`${API}/files/${fileId}?alt=media`), {
    asText: true,
    resourceKey: resourceKey ? { fileId, key: resourceKey } : undefined,
  });
}

export async function getHeadRevisionId(
  fileId: string,
  resourceKey?: string,
): Promise<string | undefined> {
  const meta = await request<FileMeta>(
    withAllDrives(`${API}/files/${fileId}?fields=headRevisionId`),
    { resourceKey: resourceKey ? { fileId, key: resourceKey } : undefined },
  );
  return meta.headRevisionId;
}

export function saveContent(
  fileId: string,
  content: string,
  resourceKey?: string,
): Promise<FileMeta> {
  return request<FileMeta>(
    withAllDrives(
      `${UPLOAD}/files/${fileId}?uploadType=media&fields=${encodeURIComponent(META_FIELDS)}`,
    ),
    {
      method: 'PATCH',
      headers: { 'Content-Type': `${CONFIG.markdownMime}; charset=UTF-8` },
      body: content,
      resourceKey: resourceKey ? { fileId, key: resourceKey } : undefined,
      retries: 2,
    },
  );
}

export interface CreateResult {
  meta: FileMeta;
  /** Igaz, ha a fájl a kért mappa helyett a Saját meghajtó gyökerébe került. */
  fellBackToRoot: boolean;
  /** Miért nem sikerült a kért mappába tenni (ha nem sikerült). */
  reason?: string;
}

/**
 * Új markdown fájl létrehozása.
 *
 * Közös meghajtón a `supportsAllDrives=true` nélkül a Drive nem találja a szülőmappát.
 * Ha a létrehozás a megadott mappába mégsem megy (jogosultság, törölt mappa), akkor
 * inkább a gyökérbe hozzuk létre — de ezt visszajelezzük, nem nyeljük le.
 */
export async function createFile(
  name: string,
  content: string,
  folderId?: string,
  folderResourceKey?: string,
): Promise<CreateResult> {
  const url = withAllDrives(`${API}/files?fields=${encodeURIComponent(META_FIELDS)}`);
  const body = (parents?: string[]) =>
    JSON.stringify({ name, mimeType: CONFIG.markdownMime, ...(parents ? { parents } : {}) });

  let meta: FileMeta;
  let fellBackToRoot = !folderId;
  let reason: string | undefined = folderId
    ? undefined
    : 'A Drive nem adott át célmappát (hiányzó folderId a state paraméterben).';

  if (folderId) {
    try {
      meta = await request<FileMeta>(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body([folderId]),
        resourceKey: folderResourceKey ? { fileId: folderId, key: folderResourceKey } : undefined,
        retries: 1,
      });
    } catch (err) {
      if (!(err instanceof DriveError) || (err.status !== 404 && err.status !== 403)) throw err;
      fellBackToRoot = true;
      reason = `A célmappa nem érhető el (${err.status}). ${err.message}`;
      meta = await request<FileMeta>(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body(),
      });
    }
  } else {
    meta = await request<FileMeta>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body(),
    });
  }

  // Ellenőrizzük, hogy tényleg a kért mappába került-e.
  if (folderId && !fellBackToRoot && meta.parents && !meta.parents.includes(folderId)) {
    fellBackToRoot = true;
    reason = 'A Drive más mappába tette a fájlt, mint amit kértünk.';
  }

  const saved = content ? await saveContent(meta.id, content) : meta;
  return { meta: { ...meta, ...saved }, fellBackToRoot, reason };
}

export function renameFile(fileId: string, name: string): Promise<FileMeta> {
  return request<FileMeta>(
    withAllDrives(`${API}/files/${fileId}?fields=${encodeURIComponent(META_FIELDS)}`),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
  );
}

/** Mentés másolatként — ütközéskezelés egyik kimenete. */
export async function saveAsCopy(
  original: FileMeta,
  content: string,
): Promise<FileMeta> {
  const base = original.name.replace(/\.(md|markdown|mdx)$/i, '');
  const ext = original.name.match(/\.(md|markdown|mdx)$/i)?.[0] ?? '.md';
  const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
  // A másolat oda kerül, ahol az eredeti van (közös meghajtón is).
  const parent = original.parents?.[0];
  const { meta } = await createFile(`${base} (másolat ${stamp})${ext}`, content, parent);
  return meta;
}
